// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Outline.tsx
 * @input Uses React, StyleX, Astryx link provider integration, Outline hooks/types
 * @output Exports Outline component and OutlineProps type
 * @position Core implementation; consumed by index.ts
 *
 * A table-of-contents navigation component with:
 * - Sliding indicator track (vertical divider + animated active bar)
 * - Density variant (default/compact)
 * - Scroll-spy active state when uncontrolled
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Outline/Outline.doc.mjs
 * - /packages/core/src/Outline/index.ts
 * - /apps/storybook/stories/Outline.stories.tsx
 * - /packages/cli/templates/blocks/components/Outline/ (showcase blocks)
 */
import { useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, durationVars, easeVars, radiusVars, spacingVars, typeScaleVars, fontWeightVars } from "../theme/tokens.stylex.js";
import { useLinkComponent } from "../Link/useLinkComponent.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { useScrollSpy } from "./useScrollSpy.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const styles = {
  root: {
    k1xSpc: "x78zum5",
    kXwgrk: "x1q0g3np",
    kVAEAm: "x1n2onr6",
    kOIVth: "x1lsbc85",
    kzqmXN: "xh8yej3",
    $$css: true
  },
  activeAnchor: {
    k48fcG: "x7dpabl",
    $$css: true
  },
  link: {
    kGNEyG: "x6s0dn4",
    kaIpWk: "xh6dtrn",
    kB7OPa: "x9f619",
    kMwMTN: "xv1l7n4",
    kkrTdU: "x1ypdohk",
    k1xSpc: "x78zum5",
    k63SB2: "x1sodnla",
    kI3sdo: "x1a2a7pz",
    kVAEAm: "x1n2onr6",
    k9WMMc: "x1yc453h",
    kybGjl: "x1hl2dhg",
    kIyJzY: "xuedmi6",
    k1ekBW: "xs2xxs2",
    kAMwcw: "xlr8y92",
    kzqmXN: "xh8yej3",
    kGuDYH: "xjm74w1",
    kLWn49: "xw6l6zx",
    kHE3J0: "xe9uy6x",
    krNwJM: "x140uwzg",
    kSReZ0: "xyxi2l3",
    k3Woio: "x17nn4n9",
    kiEn40: "x7s97pk",
    $$css: true
  },
  activeLink: {
    kMwMTN: "x1tgivj0",
    k63SB2: "x2mo6ok",
    $$css: true
  }
};
const densityStyles = {
  compact: {
    k8WAf4: "xu0wf1k",
    kwRFfy: "x1djylfy",
    $$css: true
  },
  default: {
    k8WAf4: "xce4md1",
    kwRFfy: "x1djylfy",
    $$css: true
  }
};
const indentStyles = {
  level1: {
    kZCmMZ: "x126nfab",
    $$css: true
  },
  level2: {
    kZCmMZ: "xchaq28",
    $$css: true
  },
  level3: {
    kZCmMZ: "xc8afjc",
    $$css: true
  },
  level4: {
    kZCmMZ: "x19b7t93",
    $$css: true
  }
};
function getIndentStyle(level) {
  // Map heading levels 1-6 to visual indent levels 1-4
  // Level 1 (h1) = indent 1, Level 2 (h2) = indent 1, Level 3 (h3) = indent 2, etc.
  const indentLevel = Math.max(1, Math.min(4, level - 1 || 1));
  switch (indentLevel) {
    case 1:
      return indentStyles.level1;
    case 2:
      return indentStyles.level2;
    case 3:
      return indentStyles.level3;
    default:
      return indentStyles.level4;
  }
}

// =============================================================================
// Component
// =============================================================================

/**
 * A table-of-contents navigation component for document headings.
 *
 * Outline accepts a flat `items` array and renders anchor links with
 * indentation based on each heading level. Features a sliding indicator
 * track that animates to the active item.
 *
 * When `activeId` is omitted, it tracks scroll position and marks the last
 * heading whose top has passed its activation line (its scroll-margin-top)
 * active — defaulting to the first item at the top and the last at the bottom.
 *
 * @example
 * ```
 * <Outline
 *   items={[
 *     {id: 'intro', label: 'Introduction', level: 1},
 *     {id: 'features', label: 'Features', level: 2},
 *     {id: 'api', label: 'API Reference', level: 1},
 *   ]}
 * />
 * ```
 */
export function Outline({
  items,
  activeId,
  onActiveIdChange,
  label: labelFromProps,
  density = 'default',
  xstyle,
  className,
  style,
  ref,
  'data-testid': testId,
  ...props
}) {
  const t = useTranslator();
  const label = labelFromProps ?? t('@astryx.outline.label');
  const rootRef = useRef(null);
  const LinkComponent = useLinkComponent();
  const isControlled = activeId !== undefined;
  const {
    activeId: resolvedActiveId,
    setActiveId,
    lockActiveId
  } = useScrollSpy({
    activeId,
    items,
    onActiveIdChange,
    rootRef
  });
  const handleClick = id => event => {
    const target = document.getElementById(id);

    // Let the browser handle modified clicks (open in new tab, etc.) and
    // missing targets without touching the active state.
    if (target == null || event.defaultPrevented || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) {
      return;
    }
    event.preventDefault();
    window.history.pushState(null, '', `#${id}`);

    // Move the indicator to the clicked item in a single step. Controlled
    // consumers own the active state (notify only); uncontrolled mode pins
    // the active id and suppresses scroll-spy until the next manual scroll,
    // so the click is honored — even for short/last sections — and the
    // indicator doesn't chase the smooth scroll through other sections.
    if (isControlled) {
      setActiveId(id);
    } else {
      lockActiveId(id);
    }
    target.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return /*#__PURE__*/_jsxs("nav", {
    ...props,
    ref: mergeRefs(rootRef, ref),
    "aria-label": label,
    "data-testid": testId,
    ...mergeProps(themeProps('outline', {
      density
    }), stylex.props(styles.root, xstyle), className, style),
    children: [/*#__PURE__*/_jsx("ul", {
      ...{
        className: "x78zum5 xdt5ytf x1lsbc85 x1ghz6dp x1717udv xe8uvvx x98rzlu xeuugli"
      },
      role: "list",
      children: items.map(item => {
        const isActive = item.id === resolvedActiveId;
        return /*#__PURE__*/_jsx("li", {
          ...{
            className: "x3ct3a4 x1ghz6dp x1717udv"
          },
          role: "listitem",
          children: /*#__PURE__*/_jsx(LinkComponent, {
            href: `#${item.id}`,
            "aria-current": isActive ? 'true' : undefined,
            onClick: handleClick(item.id),
            ...mergeProps(themeProps('outline-item', {
              active: isActive ? 'active' : null,
              level: item.level
            }), stylex.props(styles.link, densityStyles[density], getIndentStyle(item.level), isActive && styles.activeLink, isActive && styles.activeAnchor)),
            children: /*#__PURE__*/_jsx("span", {
              ...{
                className: "xb3r6kr xlyipyv xuxw1ft"
              },
              children: item.label
            })
          })
        }, item.id);
      })
    }), /*#__PURE__*/_jsx("div", {
      ...{
        className: "x1n2onr6 xfo62xy x2lah0s x1clqncf"
      },
      "aria-hidden": "true",
      children: /*#__PURE__*/_jsx("span", {
        ...{
          className: "x10l6tqk x13vifvy x1ey2m1c x1o0tod xfo62xy x1m4xfpy xjspbzw x47corl"
        }
      })
    }), /*#__PURE__*/_jsx("span", {
      ...mergeProps(themeProps('outline-indicator'), {
        className: "x10l6tqk x1o0tod xfo62xy xowkcby xjspbzw x47corl x1vjfegm x1tsffl5 x1ltwjim x1qjb5ga x1xuz8iz xkvfbh3 xlr8y92"
      }),
      "aria-hidden": "true"
    })]
  });
}
Outline.displayName = 'Outline';